/* ============================================================================
 * AI 决策助手
 *   F-15  V2 副驾常驻 453.6px（@1680 占 27%），承载 3 张静态播报卡；
 *         @1280 时辅助栏合计吃掉 46.3%，主内容仅剩 688px、主力表格被压到 357px。
 *         V3 改为：默认收起、宽度上限 22%、≤1280px 转抽屉浮层。
 *   B-11  V2 副驾只有 5 个方法、只播报不介入。V3 的方向是「行动信封」——
 *         AI 只产候选，不可逆动作必须由人在界面上批准。
 *         接真实模型在阶段 4，此处先把交互骨架与"候选/已背书"状态语言立起来。
 * ========================================================================== */
import { useState } from 'react';
import { useUi } from '@/store/ui';
import { Icon } from './Icon';
import { Button } from './primitives';
import { toast } from './Toast';

interface Suggestion {
  id: string;
  text: string;
  source: string;
  /** 候选 = AI 产出未经人确认；已背书 = 人已采纳 */
  endorsed: boolean;
  action?: { label: string; run: () => void };
}

const SEED: Suggestion[] = [
  {
    id: 's1',
    text: '本月派单 46 单已求解，加权毛利 1308 元/吨，双层一致性待真实内核校验。',
    source: '演示内核 · 08-07 03:25',
    endorsed: false,
  },
  {
    id: 's2',
    text: '齐套率 89.4% 基于当前在途按期到货假设；高硫焦到港延迟将击穿 80% 门槛。',
    source: '演示内核 · 08-07 03:25',
    endorsed: false,
  },
  {
    id: 's3',
    text: '波动中心有 2 件 L2 待签注（签注即触发增量求解）。',
    source: '演示内核 · 08-07 03:25',
    endorsed: false,
  },
];

export function Copilot() {
  const { ai, toggleAi } = useUi();
  const [items, setItems] = useState(SEED);
  const [q, setQ] = useState('');

  if (ai !== 'open') return null;

  const endorse = (id: string) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, endorsed: true } : x)));
    toast('已背书该候选建议', 'ok');
  };

  return (
    <aside className="copilot" data-mode="drawer" aria-label="AI 决策助手">
      <header className="copilot-hd">
        <div className="col" style={{ gap: 2 }}>
          <div className="row">
            <Icon name="bot" size={15} />
            <b>AI 决策助手</b>
          </div>
          <div className="t-aux">AI 只产候选 · 不可逆动作由人批准</div>
        </div>
        <button className="iconbtn" onClick={toggleAi} aria-label="收起 AI 决策助手">
          <Icon name="close" />
        </button>
      </header>

      <div className="copilot-bd">
        {items.map((s) => (
          <article key={s.id} className={`aicard${s.endorsed ? ' is-endorsed' : ''}`}>
            <p className="aicard-text">{s.text}</p>
            <div className="aicard-ft">
              {/* F-11：V2 把来源行做成 10px + .28 透明度（深色 2.49:1 / 浅色 1.78:1），
                  一个把"数据可追溯"当卖点的系统，来源反而是全站最看不清的字。 */}
              <span className="aicard-src">{s.source}</span>
              <span className={`badge ${s.endorsed ? 'badge--ok' : 'badge--warn'}`}>
                {s.endorsed ? '已背书' : '候选 · 未背书'}
              </span>
            </div>
            {!s.endorsed && (
              <Button size="sm" variant="ghost" onClick={() => endorse(s.id)}>采纳为已背书</Button>
            )}
          </article>
        ))}
      </div>

      <form
        className="copilot-ft"
        onSubmit={(e) => {
          e.preventDefault();
          if (!q.trim()) return;
          toast('真实模型接入在阶段 4，当前为交互骨架', 'warn');
          setQ('');
        }}
      >
        <input
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="问 AI 助手：如「齐套缺口在哪」"
          aria-label="AI 助手输入框"
        />
        <Button size="sm" variant="primary" type="submit">发送</Button>
      </form>
    </aside>
  );
}
